'use client';

import { forwardRef, useContext, useEffect } from 'react';

import { css, cx } from '@/styled-system/css';

import { DateTimeBlockRootContext } from './context/date-time-block-root.context';
import { useDateTimeBlockDisabledItems } from './hooks/use-disabled-items';
import { useDateTimeBlockRenderTimes } from './hooks/use-render-times';
import type { DateTimeBlockTimesPropsInterface } from './types/date-time-block-times.types';

/**
 * `DateTimeBlock.Times` provides the interface for selecting times from the available
 * options provided by the root context. Users can select between a group of chips or
 * a select dropdown based on the `timeDisplay` prop
 *
 * @since 10.2.0
 */
export const DateTimeBlockTimes = forwardRef<HTMLDivElement, DateTimeBlockTimesPropsInterface>(
  (props: DateTimeBlockTimesPropsInterface, forwardedRef) => {
    const {
      children,
      className,
      css: cssProp,
      renderTimesChips,
      renderTimesSelect,
      renderTimesHelp,
      ...rest
    } = props;

    const {
      timeDisplay,
      selectedDate,
      selectedTime,
      times,
      onTimeChange,
      disabledTimes,
      formFieldContext,
      multiple,
      setHasTimesComponent,
    } = useContext(DateTimeBlockRootContext);

    // Register this component as being rendered (Times is always primary when present)
    useEffect(() => {
      if (times && times.length > 0) {
        setHasTimesComponent(true);
        return () => setHasTimesComponent(false);
      }
    }, [setHasTimesComponent, times]);

    const isTimeDisabled = useDateTimeBlockDisabledItems(disabledTimes);

    // Use hook to render times components
    const { chipsElement, selectElement, helpElement } = useDateTimeBlockRenderTimes({
      times,
      selectedTime,
      onTimeChange,
      isTimeDisabled,
      formFieldContext,
      multiple,
      renderTimesChips,
      renderTimesSelect,
      renderTimesHelp,
    });

    // Don't render if no times provided
    if (!times || times.length === 0) {
      return null;
    }

    return (
      <div
        ref={forwardedRef}
        data-c11n-component="DateTimeBlock.Times"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', css(cssProp), className)}
        {...rest}
      >
        {!selectedDate && helpElement}
        {selectedDate && timeDisplay === 'chips' && chipsElement}
        {selectedDate && timeDisplay === 'select' && selectElement}
        {children}
      </div>
    );
  },
);

DateTimeBlockTimes.displayName = 'DateTimeBlock.Times';
